import {
  formatHeroCurrency,
  getComparableOfferPrice,
  getOfferLink,
  getRetailerNameFromOffer,
} from '../PriceComparison/simpleProductPageShared';
import {
  formatVerificationDate,
  getOfferListPresentation,
  getOfferVerificationTimestamp,
  selectCurrentVerifiedBest,
  sortCustomerOffers,
} from '../../domain/offerFreshness';

export const COMPARISON_RETAILER_CTA = 'Check price & availability';

const CENTRAL_TIME = 'America/Chicago';

export function formatComparisonTimestamp(offer, prefix = 'Updated') {
  const ts = getOfferVerificationTimestamp(offer);
  if (ts == null) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CENTRAL_TIME,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(ts));
  const get = (type) => parts.find((part) => part.type === type)?.value || '';
  const date = `${get('month')} ${get('day')}, ${get('year')}`;
  const time = `${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
  return `${prefix} ${date} at ${time} CT`;
}

export function getOtherRetailerOffers(offers, primaryOffer) {
  const sorted = sortCustomerOffers(offers);
  if (!primaryOffer) return sorted;
  const primaryHref = getOfferLink(primaryOffer);
  const primaryRetailer = getRetailerNameFromOffer(primaryOffer);
  return sorted.filter((offer) => {
    const href = getOfferLink(offer);
    const retailer = getRetailerNameFromOffer(offer);
    if (primaryHref && href) return href !== primaryHref;
    return retailer !== primaryRetailer;
  });
}

export function filterValidComparisonOffers(offers, options = {}) {
  if (!Array.isArray(offers)) return [];
  return sortCustomerOffers(
    offers.filter((offer) => getComparableOfferPrice(offer, options) != null),
    options
  );
}

export function formatOfferPrice(offer) {
  return formatHeroCurrency(getComparableOfferPrice(offer));
}

export function formatOfferFreshness(offer) {
  return formatVerificationDate(offer);
}

export function formatOfferAvailability(offer) {
  return getOfferListPresentation(offer).availabilityLabel;
}

function offerRowKey(offer) {
  return `${getRetailerNameFromOffer(offer)}|${getOfferLink(offer) || ''}`;
}

export function getComparisonOfferSummary(offers) {
  const selection = selectCurrentVerifiedBest(offers);
  const offer = selection.primary;

  if (!selection.hasCurrent || !offer) {
    return {
      hasCurrent: false,
      isTie: false,
      uniqueLowest: false,
      kicker: 'Current price unavailable',
      tieNote: null,
      ctaLabel: COMPARISON_RETAILER_CTA,
      offer: null,
      priceLabel: null,
      retailer: null,
      freshness: null,
      href: '',
      salePrice: null,
      otherOffers: getOtherRetailerOffers(offers, null),
    };
  }

  return {
    hasCurrent: true,
    isTie: selection.isTie,
    uniqueLowest: selection.uniqueLowest,
    kicker: null,
    tieNote: null,
    ctaLabel: COMPARISON_RETAILER_CTA,
    offer,
    priceLabel: formatOfferPrice(offer),
    retailer: getRetailerNameFromOffer(offer),
    freshness: formatComparisonTimestamp(offer, 'Updated'),
    href: getOfferLink(offer) || '',
    salePrice: getComparableOfferPrice(offer),
    otherOffers: getOtherRetailerOffers(offers, offer),
  };
}

export function getComparisonPriceBoard(offers) {
  const summary = getComparisonOfferSummary(offers);
  const selection = selectCurrentVerifiedBest(offers);
  const tiedKeys = new Set((selection.tied || []).map(offerRowKey));
  const rows = sortCustomerOffers(offers).map((offer) => {
    const presentation = getOfferListPresentation(offer);
    const key = offerRowKey(offer);
    return {
      offer,
      retailer: getRetailerNameFromOffer(offer),
      href: getOfferLink(offer) || '',
      presentation,
      isTiedBest: tiedKeys.has(key),
      isPrimary: summary.offer ? offerRowKey(summary.offer) === key : false,
    };
  });

  return {
    ...summary,
    rows,
    remainingRows: rows.filter((row) => !row.isPrimary),
  };
}

export {
  getOfferLink,
  getRetailerNameFromOffer,
  getComparableOfferPrice,
  getOfferListPresentation,
  selectCurrentVerifiedBest,
  sortCustomerOffers,
};
